import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Ship, TrendingUp, DollarSign, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProductSelectorPage() {
  const navigate = useNavigate();

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, hsl(var(--background)) 0%, hsl(var(--muted))/20 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '1200px', width: '100%' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <div style={{ 
            fontSize: '0.875rem', 
            fontWeight: 600,
            color: '#2EE6A6',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            marginBottom: '1rem'
          }}>
            Sagisu Intelligence Platform
          </div>
          <h1 style={{ 
            fontSize: '2.5rem', 
            fontWeight: 700,
            marginBottom: '1rem'
          }}>
            Choose Your Platform
          </h1>
          <p style={{ 
            fontSize: '1.125rem',
            color: 'hsl(var(--muted-foreground))',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Two specialized platforms for real-time trade intelligence
          </p>
        </div>

        {/* Product Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {/* Maritime Pulse */}
          <Card 
            className="cursor-pointer hover:shadow-2xl transition-all border-2 border-[hsl(var(--accent))] hover:scale-105"
            onClick={() => navigate('/dashboard')}
            data-testid="select-maritime"
          >
            <div style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '4px',
              background: 'hsl(var(--accent))'
            }} />
            <CardHeader>
              <div style={{ 
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#2EE6A6',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                marginBottom: '0.5rem'
              }}>
                Logistics Intelligence
              </div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <Ship className="h-6 w-6 text-[hsl(var(--accent))]" />
                Maritime Pulse
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Global vessel tracking, freight analytics, port insights, and marketplace intelligence for real-time maritime operations.
              </p>
              <ul className="space-y-2 text-sm text-[hsl(var(--muted-foreground))]">
                <li className="flex items-start gap-2">
                  <span className="text-[hsl(var(--accent))] mt-0.5">•</span>
                  <span>Track 12,709 vessels in real-time</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[hsl(var(--accent))] mt-0.5">•</span>
                  <span>Freight insurance & risk assessment</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[hsl(var(--accent))] mt-0.5">•</span>
                  <span>AI-powered vessel-cargo matching</span>
                </li>
              </ul>
              <div className="pt-4 border-t flex items-center justify-between">
                <span className="text-xs text-[hsl(var(--muted-foreground))]" style={{ fontFamily: 'monospace' }}>
                  LIVE • 80+ Endpoints
                </span>
                <ArrowRight className="h-4 w-4 text-[hsl(var(--accent))]" />
              </div>
            </CardContent>
          </Card>

          {/* Commerce Index */}
          <Card 
            className="cursor-pointer hover:shadow-2xl transition-all border-2 border-[hsl(var(--primary))] hover:scale-105 shadow-lg"
            onClick={() => navigate('/commerce')}
            data-testid="select-commerce"
            style={{ background: 'hsl(var(--primary))/5' }}
          >
            <div style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '4px',
              background: 'hsl(var(--primary))'
            }} />
            <CardHeader>
              <div style={{ 
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#2EE6A6',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                marginBottom: '0.5rem'
              }}>
                Commerce Intelligence
              </div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <TrendingUp className="h-6 w-6 text-[hsl(var(--primary))]" />
                Commerce Index
                <Badge variant="secondary" className="ml-2 text-xs">Preview</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Real-time pulse of global commerce — track demand signals, breakout products, and market momentum in one benchmark.
              </p>
              <p className="text-xs text-[hsl(var(--muted-foreground))] italic mt-2">
                Demo data for illustration • Full launch Q1 2025
              </p>
              <ul className="space-y-2 text-sm text-[hsl(var(--muted-foreground))]">
                <li className="flex items-start gap-2">
                  <span className="text-[hsl(var(--primary))] mt-0.5">•</span>
                  <span>CI score (0–1000) with 6 sub-indexes</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[hsl(var(--primary))] mt-0.5">•</span>
                  <span>Category heatmaps & trend analysis</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[hsl(var(--primary))] mt-0.5">•</span>
                  <span>Breakout SKU detection & forecasts</span>
                </li>
              </ul>
              <div className="pt-4 border-t flex items-center justify-between">
                <span className="text-xs text-[hsl(var(--muted-foreground))]" style={{ fontFamily: 'monospace' }}>
                  LIVE • Real-Time Data
                </span>
                <ArrowRight className="h-4 w-4 text-[hsl(var(--primary))]" />
              </div>
            </CardContent>
          </Card>

          {/* Trade & Finance - Coming Soon */}
          <Card 
            className="opacity-75 border-2 border-dashed border-[hsl(var(--border))]"
            style={{ gridColumn: 'span 2' }}
          >
            <div style={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '4px',
              background: 'linear-gradient(to right, hsl(var(--primary)), hsl(var(--accent)))'
            }} />
            <CardHeader>
              <div style={{ 
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#2EE6A6',
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                marginBottom: '0.5rem'
              }}>
                Finance Intelligence
              </div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <DollarSign className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />
                Trade & Finance
                <Badge variant="secondary" className="ml-2">Coming Soon</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">
                Tokenization, lending, letters of credit, and prediction markets for global trade — built on real-world commerce and logistics signals.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer Note */}
        <div style={{ textAlign: 'center' }}>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Powered by unified data intelligence • Sagisu Platform v1.0
          </p>
        </div>
      </div>
    </div>
  );
}
